import _keyBy from 'lodash/keyBy';
import _isEqual from 'lodash/isEqual';

import * as Sift from '@sift/types';
import { FilterField } from 'pages/RolesAndPermissions/typings';
import isFieldTextCollection from 'helpers/isFieldTextCollection';
import { MATCH_ALL_FILTER } from 'pages/RolesAndPermissions/consts';

function buildFilterFromAPI(apiFilter: any, fields: Sift.Field[]) {
  const fieldsByObjectKey = _keyBy(fields, 'objectKey');

  // If there's no filter or it's the match all filter, initiate
  // the UI filter groups to be empty
  if (!apiFilter || _isEqual(apiFilter, MATCH_ALL_FILTER)) {
    return { operator: 'and', filters: [] };
  }

  const resolveOperator = (statement: any) =>
    statement.and ? 'and' : statement.or ? 'or' : undefined;
  const baseOperator = resolveOperator(apiFilter);

  if (!baseOperator) {
    // This is for the case where the API format has no operator, and is a single statement. The UI probably would not generate this format,
    // but it's technically supported
    if (apiFilter.field) {
      return {
        operator: 'and',
        filters: [
          {
            operator: 'and',
            filters: [
              {
                ...apiFilter,
                id: 'filter1',
                field: getFilterFieldForObjectKey(apiFilter.field, fieldsByObjectKey),
                comparison: apiFilter.comparison || 'eq',
                value:
                  apiFilter.value === true
                    ? 'true'
                    : apiFilter.value === false
                    ? 'false'
                    : apiFilter.value,
              },
            ],
            id: 'group1',
          },
        ],
      };
    }

    // If we reach this area, the data structure is malformed. We could either throw an error or return an empty filter
    return { operator: 'and', filters: [] };
  }

  const _filters = apiFilter[baseOperator].map((statementOrGroup: any) => {
    const innerOperator = resolveOperator(statementOrGroup);
    const innerStatements = innerOperator ? statementOrGroup[innerOperator] : [statementOrGroup];

    const innerFilters = innerStatements;

    return { operator: innerOperator || 'and', filters: innerFilters };
  });

  const filters = _filters.map((group: any, index: number) => ({
    ...group,
    id: `group${index + 1}`,
    filters: group.filters.map((filter: any, fIndex: number) => ({
      ...filter,
      id: `group${index + 1}-filter${fIndex + 1}`,
      field: getFilterFieldForObjectKey(filter.field, fieldsByObjectKey),
      value: filter.value === true ? 'true' : filter.value === false ? 'false' : filter.value,
      comparison: filter.comparison || 'eq',
    })),
  }));

  return { operator: baseOperator, filters };
}

function getFilterFieldForObjectKey(
  objectKey: string,
  fieldsByObjectKey: { [objectKey: string]: Sift.Field },
): FilterField {
  const matchingField = fieldsByObjectKey[objectKey];

  if (!matchingField) {
    // NOTE(rbickham11): We should leave this condition here since it's technically possible for a field to be
    // deleted and this filter to still exist. "type" is currently required on FilterFields, so I set it
    // as "unknown" if we want to handle this elsewhere. We could also update the type
    return { displayName: objectKey, objectKey, type: 'unknown' };
  }

  const { displayName, type } = matchingField;

  return {
    displayName,
    objectKey,
    type: isFieldTextCollection(matchingField) ? 'textCollection' : type,
  };
}

export default buildFilterFromAPI;
